import React, { useEffect, useRef } from 'react';
import { Carousel } from 'antd';
import './MarketplaceOfferModule.scss';
import * as carouselConfigs from './carouselConfig';
import { ReactComponent as ChevronLeft } from 'style/_assets/icons/chevron-left.svg';
import { ReactComponent as ChevronRight } from 'style/_assets/icons/chevron-right.svg';
import { SUGGESTED_ACTIONS } from 'types/morph.pagePosition.constants';
import {
  isEnterKey,
  useCarouselAccessibility,
  useSeenModulePixel,
} from 'hook/helper.hook';
import { getViewModuleTrackingAttributes } from 'helpers/tracking.helpers';
import { setAriaHiddenOnSlides } from 'components/Fragments/OfferCarousel/OfferCarousel';

const Arrows = props => {
  const { onClick, isNext, triggerCarouselReRender, handleFocusOnElement } =
    props;
  const icon = isNext ? (
    <ChevronRight className="chevronCarousel" />
  ) : (
    <ChevronLeft className="chevronCarousel" />
  );
  if (onClick) {
    return (
      <div
        className={`${
          isNext ? `slickArrowNext` : `slickArrowPrev`
        } slickArrows`}
        onClick={() => {
          onClick();
          triggerCarouselReRender();
        }}
        onKeyDown={e => {
          if (isEnterKey(e)) {
            onClick();
            triggerCarouselReRender();
            handleFocusOnElement();
          }
        }}
        tabIndex={0}
        role="button"
        aria-label="carousel_button"
      >
        <div className="oval">{icon}</div>
      </div>
    );
  }
  return null;
};

const MarketplaceOfferDesktop = function ({
  carouselRoot,
  pageClass,
  datatestid,
  children,
  slickProps,
  customClass,
  seenPixel,
}) {
  const { handleFocusOnElement, triggerCarouselReRender } =
    useCarouselAccessibility({ carouselRoot });
  return (
    <div
      ref={carouselRoot}
      className={`rootCarousel format100 mobileOnlyHide showArrows ${pageClass} ${customClass}`}
    >
      <div className="margL40">{seenPixel}</div>
      <div
        data-testid={
          datatestid ? `${datatestid}-marketplaceModuleDesktop` : null
        }
        className="slick-action-list"
      >
        <Carousel
          prevArrow={
            <Arrows
              triggerCarouselReRender={triggerCarouselReRender}
              handleFocusOnElement={handleFocusOnElement}
            />
          }
          nextArrow={
            <Arrows
              isNext
              triggerCarouselReRender={triggerCarouselReRender}
              handleFocusOnElement={handleFocusOnElement}
            />
          }
          {...slickProps}
        >
          {children}
        </Carousel>
      </div>
    </div>
  );
};

const MarketplaceOfferMobile = function ({
  children,
  datatestid,
  customClass,
  seenPixel,
}) {
  return (
    <div className={`format100 nonMobileHide ${customClass}`}>
      <div className="margL40">{seenPixel}</div>
      <div
        data-testid={
          datatestid ? `${datatestid}-MarketplaceModuleMobile` : null
        }
        className="marketplaceCarouselMobileInner FlexContainer"
      >
        {children &&
          children.map((item, i) => {
            return (
              <div className="marketplaceContainer" key={i}>
                <div className="marketplaceContainerInner">{item}</div>
              </div>
            );
          })}
      </div>
    </div>
  );
};

const MarketPlaceCarousel = function ({
  children,
  datatestid,
  show,
  type,
  slidesToShow,
  ...props
}) {
  let carouselSizeMinusTwo = useRef(0);
  let pageClass = carouselConfigs.getClassByPage(type);
  let slickProps = carouselConfigs.getSlickPropsByPage(type);

  const carouselRoot = useRef();

  let customClass = '';
  const atr = getViewModuleTrackingAttributes(
    props.customModuleName ?? SUGGESTED_ACTIONS,
    props.modulePosition,
  );
  const seenPixel = useSeenModulePixel(atr);

  if (props.isSmallActions) {
    customClass = 'smallActions';
    slickProps = Object.assign({}, slickProps);
    slickProps.slidesToShow = slidesToShow ? slidesToShow : 5;
  }

  useEffect(() => {
    if (children.length > 0) {
      const maxVisibleChildren = props.isSmallActions ? 5 : 3;
      carouselSizeMinusTwo.current = children.length - maxVisibleChildren;

      if (carouselRoot && carouselRoot.current) {
        let firstSlide =
          carouselRoot.current.querySelectorAll('[data-index="0"]');
        let thirdToLastSlide = carouselRoot.current.querySelectorAll(
          '[data-index="' + carouselSizeMinusTwo.current + '"]',
        );
        setAriaHiddenOnSlides(carouselRoot);
        const config = { attributes: true };
        const observer = new MutationObserver(mutationCallback);
        if (firstSlide[0] && thirdToLastSlide[0]) {
          observer.observe(firstSlide[0], config);
          observer.observe(thirdToLastSlide[0], config);
        }
      }
    }
    //eslint-disable-next-line
  }, [children]);

  const mutationCallback = function (mutationsList, observer) {
    mutationsList
      .filter(m => m.attributeName === 'class')
      .forEach(mutation => {
        const mutatedDataIndexValue =
          mutation.target['attributes']['data-index']['value'];
        const carouselSizeMinusTwoString = String(carouselSizeMinusTwo.current);

        if (mutatedDataIndexValue === '0') {
          handleFirstSlideMutation(carouselRoot.current, mutation);
        } else if (mutatedDataIndexValue === carouselSizeMinusTwoString) {
          handleLastSlideMutation(carouselRoot.current, mutation);
        }
      });
  };

  return (
    <>
      <MarketplaceOfferDesktop
        carouselRoot={carouselRoot}
        pageClass={pageClass}
        datatestid={datatestid}
        slickProps={slickProps}
        children={children}
        customClass={customClass}
        seenPixel={seenPixel}
      />
      <MarketplaceOfferMobile
        pageClass={pageClass}
        datatestid={datatestid}
        children={children}
        customClass={customClass}
        seenPixel={seenPixel}
      />
    </>
  );
};

/**
 *
 * @private
 *
 */

const handleFirstSlideMutation = (ref, mutation) => {
  if (ref.getElementsByClassName('slickArrowPrev')[0]) {
    if (mutation.target.classList.contains('slick-current')) {
      ref.getElementsByClassName('slickArrowPrev')[0].classList.add('poof');
    } else {
      ref.getElementsByClassName('slickArrowPrev')[0].classList.remove('poof');
    }
  }
};

const handleLastSlideMutation = (ref, mutation) => {
  if (ref.getElementsByClassName('slickArrowNext')[0]) {
    if (mutation.target.classList.contains('slick-current')) {
      ref.getElementsByClassName('slickArrowNext')[0].classList.add('poof');
    } else {
      ref.getElementsByClassName('slickArrowNext')[0].classList.remove('poof');
    }
  }
};

export default MarketPlaceCarousel;
